import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CerebroTextButton } from '../../../shared/components';
import { getResponseData } from '../../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { TermSheetFacility, TermSheetFacilityCollateral } from '../../../shared/models';

interface DeleteCollateralDialogProps {
	dealGuid: string | undefined;
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	collateral: TermSheetFacilityCollateral | null;
	close: () => void;
}

export const DeleteCollateralDialog: React.FC<DeleteCollateralDialogProps> = ({
	dealGuid,
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	collateral,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (collateral !== null) deleteCollateral(collateral);
	};

	const { mutate: deleteCollateral } = useMutation<void, unknown, TermSheetFacilityCollateral>(
		collateralToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.SpecificFacilityCollateral(
						dataRoomGuid,
						termSheetGuid,
						revisionGuid,
						facility?.guid,
						collateralToDelete?.guid
					)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(
					QueryKeys.TermSheets.FacilityCollateral(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid)
				);
				client.invalidateQueries(QueryKeys.TermSheets.Specific(dealGuid, termSheetGuid));
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dealGuid, termSheetGuid, revisionGuid));
				snackbar.enqueueSnackbar('Facility collateral successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the facility collateral.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={collateral !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete the facility collateral?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
